import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { authService, clasesService, cursosService } from '../services/api';
import RichTextEditor from '../components/RichTextEditor';
import CodeEditor from '../components/CodeEditor';
import UploadProgress from '../components/UploadProgress';
import HtmlContent from '../components/HtmlContent';
import ConfirmPopup from '../components/ConfirmPopup';
import './Clases.css';

const getYoutubeEmbedUrl = (url) => {
  const s = String(url || '').trim();
  const match = s.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return match ? `https://www.youtube.com/embed/${match[1]}` : null;
};

const getTipoMaterialLabel = (tipo) => {
  const labels = { archivo: '📎 Archivo', enlace: '🔗 Enlace', codigo: '💻 Código', texto: '📝 Texto', video: '🎬 Video' };
  return labels[tipo] || tipo;
};

const LENGUAJES_CODIGO = [
  { value: 'plaintext', label: 'Texto plano' },
  { value: 'python', label: 'Python' },
  { value: 'javascript', label: 'JavaScript' },
  { value: 'typescript', label: 'TypeScript' },
  { value: 'html', label: 'HTML' },
  { value: 'css', label: 'CSS' },
  { value: 'json', label: 'JSON' },
  { value: 'sql', label: 'SQL' },
  { value: 'java', label: 'Java' },
  { value: 'c', label: 'C' },
  { value: 'cpp', label: 'C++' },
  { value: 'php', label: 'PHP' },
  { value: 'ruby', label: 'Ruby' },
  { value: 'go', label: 'Go' },
];

const ClaseForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id && id !== 'nuevo');
  const materialArchivoInputRef = React.useRef(null);

  const [formData, setFormData] = useState({
    titulo: '',
    descripcion: '',
    fecha_programada: '',
    publicada: false,
    material: [],
    curso: null,
  });
  const [nuevoMaterial, setNuevoMaterial] = useState({ tipo: 'texto', contenido: '', titulo: '', lenguaje: 'plaintext', _file: null });
  const [cursos, setCursos] = useState([]);
  const [reutilizables, setReutilizables] = useState([]);
  const [reutilizarCodigo, setReutilizarCodigo] = useState(null);
  const [reutilizarId, setReutilizarId] = useState('');
  const [loadingReutilizar, setLoadingReutilizar] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentUser, setCurrentUser] = useState(null);
  const [isProfesor, setIsProfesor] = useState(false);
  const [isCoordinador, setIsCoordinador] = useState(false);

  useEffect(() => {
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }
    const user = authService.getCurrentUser();
    setCurrentUser(user);
    const savedRole = localStorage.getItem('selectedRole');
    setIsProfesor(savedRole === 'Profesor');
    setIsCoordinador(savedRole === 'Coordinador');
  }, [navigate]);

  useEffect(() => {
    if (currentUser === null) return;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await cursosService.getCursos();
        // Solo cursos activos
        let list = (data || []).filter(c => c.activo !== false);
        // Si no es coordinador, solo cursos donde el usuario está en el equipo (profesor, tutor, etc.)
        if (isProfesor && !isCoordinador && currentUser) {
          list = list.filter(curso => {
            if (curso.equipo && Array.isArray(curso.equipo)) {
              return curso.equipo.some(m => {
                const uid = typeof m === 'object' && m && (m.id != null || m.user_id != null) ? (m.user_id ?? m.id) : m;
                return parseInt(uid) === parseInt(currentUser.id);
              });
            }
            return false;
          });
        }
        setCursos(list);
        if (isEdit) {
          const clase = await clasesService.getClase(id);
          let fechaProgramada = '';
          if (clase.fecha_programada) {
            const fecha = new Date(clase.fecha_programada);
            fechaProgramada = fecha.toISOString().slice(0, 16);
          }
          setFormData({
            titulo: clase.titulo || '',
            descripcion: clase.descripcion || '',
            fecha_programada: fechaProgramada,
            publicada: clase.publicada || false,
            material: clase.material || [],
            curso: clase.curso || null,
          });
        }
      } catch (err) {
        setError(err.response?.data?.error || 'Error al cargar');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentUser, id, isEdit, isProfesor, isCoordinador]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleCursoChange = (e) => {
    const cursoId = e.target.value;
    setFormData(prev => ({ ...prev, curso: cursoId ? parseInt(cursoId, 10) : null }));
    setReutilizarId('');
  };

  useEffect(() => {
    if (!formData.curso) {
      setReutilizables([]);
      setReutilizarCodigo(null);
      return;
    }
    const curso = cursos.find((c) => String(c.id) === String(formData.curso));
    if (!curso?.codigo) {
      setReutilizables([]);
      setReutilizarCodigo(null);
      return;
    }
    let cancelled = false;
    setLoadingReutilizar(true);
    clasesService
      .getClasesReutilizables(formData.curso, isEdit ? id : null)
      .then((data) => {
        if (cancelled) return;
        setReutilizarCodigo(data?.codigo || curso.codigo || null);
        setReutilizables(Array.isArray(data?.items) ? data.items : []);
      })
      .catch(() => {
        if (!cancelled) {
          setReutilizables([]);
          setReutilizarCodigo(curso.codigo || null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingReutilizar(false);
      });
    return () => { cancelled = true; };
  }, [formData.curso, cursos, isEdit, id]);

  const aplicarClaseReutilizable = () => {
    const item = reutilizables.find((c) => String(c.id) === String(reutilizarId));
    if (!item) {
      setError('Seleccioná una clase de la carpeta de material para reutilizar.');
      return;
    }
    const material = (Array.isArray(item.material) ? item.material : [])
      .filter((m) => m && typeof m === 'object')
      .map(({ tipo, contenido, lenguaje, nombre_original, titulo }) => {
        const out = { tipo, contenido: contenido || '' };
        if (lenguaje) out.lenguaje = lenguaje;
        if (nombre_original) out.nombre_original = nombre_original;
        if (titulo) out.titulo = titulo;
        return out;
      });
    setFormData((prev) => ({
      ...prev,
      titulo: item.titulo || prev.titulo,
      descripcion: item.descripcion || '',
      material,
    }));
    setError('');
  };

  const handleMaterialChange = (e) => {
    const { name, value } = e.target;
    setNuevoMaterial(prev => {
      const next = { ...prev, [name]: value };
      if (name === 'tipo') {
        next.contenido = '';
        next.titulo = '';
        next.lenguaje = value === 'codigo' ? 'plaintext' : undefined;
        next._file = null;
      }
      return next;
    });
  };

  const agregarMaterial = () => {
    const contenido = typeof nuevoMaterial.contenido === 'string' ? nuevoMaterial.contenido.trim() : '';
    if (!contenido) {
      setError('No se puede agregar contenido vacio.');
      return;
    }
    if (nuevoMaterial.tipo === 'archivo' && !nuevoMaterial._file) {
      setError('Seleccione un archivo para el material');
      return;
    }
    const item = { tipo: nuevoMaterial.tipo, contenido };
    if (nuevoMaterial.tipo === 'codigo' && nuevoMaterial.lenguaje) {
      item.lenguaje = nuevoMaterial.lenguaje;
    }
    if (nuevoMaterial.tipo === 'enlace') {
      const titulo = (nuevoMaterial.titulo || '').trim();
      if (titulo) item.titulo = titulo;
    }
    if (nuevoMaterial.tipo === 'archivo' && nuevoMaterial._file) {
      item._file = nuevoMaterial._file;
    }
    setFormData(prev => ({ ...prev, material: [...prev.material, item] }));
    setNuevoMaterial({ tipo: 'texto', contenido: '', titulo: '', lenguaje: 'plaintext', _file: null });
    setError('');
  };

  const eliminarMaterial = (index) => {
    setFormData(prev => ({ ...prev, material: prev.material.filter((_, i) => i !== index) }));
  };

  const moverMaterial = (index, dir) => {
    if (dir === 'up' && index <= 0) return;
    if (dir === 'down' && index >= formData.material.length - 1) return;
    const newIndex = dir === 'up' ? index - 1 : index + 1;
    setFormData(prev => {
      const arr = [...prev.material];
      [arr[index], arr[newIndex]] = [arr[newIndex], arr[index]];
      return { ...prev, material: arr };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    setUploadProgress(0);
    const progressOpts = { onUploadProgress: (percent) => setUploadProgress(percent) };
    try {
      if (!formData.curso) {
        setError('Debe seleccionar un curso');
        return;
      }
      const basePayload = {
        ...formData,
        curso: formData.curso,
        fecha_programada: formData.publicada ? null : (formData.fecha_programada || null),
      };
      const tieneArchivos = (basePayload.material || []).some(m => m._file);
      if (tieneArchivos) {
        const materialParaJson = basePayload.material.map(({ _file, ...rest }) => rest);
        const formDataToSend = new FormData();
        formDataToSend.append('titulo', basePayload.titulo || '');
        formDataToSend.append('descripcion', basePayload.descripcion || '');
        formDataToSend.append('curso', basePayload.curso ?? '');
        formDataToSend.append('fecha_programada', basePayload.fecha_programada ?? '');
        formDataToSend.append('publicada', basePayload.publicada ? 'true' : 'false');
        formDataToSend.append('material', JSON.stringify(materialParaJson));
        let archivoIdx = 0;
        for (const m of basePayload.material) {
          if (m.tipo === 'archivo') {
            if (m._file) {
              formDataToSend.append('material_archivo_' + archivoIdx, m._file);
            }
            archivoIdx += 1;
          }
        }
        if (isEdit) {
          await clasesService.updateClase(id, formDataToSend, progressOpts);
        } else {
          await clasesService.createClase(formDataToSend, progressOpts);
        }
      } else {
        if (isEdit) {
          await clasesService.updateClase(id, basePayload);
        } else {
          await clasesService.createClase(basePayload);
        }
      }
      setUploadProgress(100);
      navigate('/dashboard/clases');
    } catch (err) {
      const errorData = err.response?.data;
      let errorMessage = 'Error al guardar la clase';
      if (typeof errorData === 'string' && errorData.trim()) {
        errorMessage = errorData.trim();
      } else if (errorData && typeof errorData === 'object') {
        errorMessage = errorData.error || errorMessage;
        const errObj = errorData.errors || errorData;
        if (typeof errObj === 'object' && errObj !== null && !Array.isArray(errObj)) {
          const parts = [];
          Object.keys(errObj).forEach(key => {
            const v = errObj[key];
            if (key === 'error') return;
            if (Array.isArray(v) && v.length > 0) parts.push(`${key}: ${v[0]}`);
            else if (typeof v === 'string') parts.push(`${key}: ${v}`);
          });
          if (parts.length > 0) errorMessage = parts.join(' | ');
        }
      }
      if (err.response?.status === 500) {
        console.error('Error 500 crear clase:', err.response?.data);
      }
      setError(errorMessage);
    } finally {
      setSubmitting(false);
      setUploadProgress(0);
    }
  };

  if (loading) {
    return (
      <div className="clases-container">
        <div className="clases-loading"><div className="loading-spinner"></div><p>Cargando...</p></div>
      </div>
    );
  }

  const renderPreviewMaterial = (mat, idx) => {
    const contenido = mat.contenido || '';
    const tipo = (mat.tipo || '').toLowerCase();
    const esUrl = /^https?:\/\//i.test(String(contenido).trim());
    if (tipo === 'codigo') {
      return (
        <div key={idx} className="clases-preview-material-item">
          <span className="clases-preview-material-badge">{getTipoMaterialLabel(mat.tipo)}</span>
          <pre className="clases-preview-codigo"><code>{contenido}</code></pre>
        </div>
      );
    }
    if (tipo === 'video') {
      const embedUrl = getYoutubeEmbedUrl(contenido);
      return (
        <div key={idx} className="clases-preview-material-item">
          <span className="clases-preview-material-badge">{getTipoMaterialLabel(mat.tipo)}</span>
          {embedUrl ? (
            <div className="clases-preview-video-wrap">
              <iframe title="Vista previa video" src={embedUrl} className="clases-preview-video" allowFullScreen />
            </div>
          ) : (
            <a href={contenido.trim()} target="_blank" rel="noopener noreferrer" className="clases-preview-enlace">Ver video: {contenido.trim()}</a>
          )}
        </div>
      );
    }
    if (tipo === 'enlace' && esUrl) {
      const textoVisible = (mat.titulo || '').trim() || contenido.trim();
      return (
        <div key={idx} className="clases-preview-material-item">
          <span className="clases-preview-material-badge">{getTipoMaterialLabel(mat.tipo)}</span>
          <a href={contenido.trim()} target="_blank" rel="noopener noreferrer" className="clases-preview-enlace" title={contenido.trim()}>{textoVisible}</a>
        </div>
      );
    }
    if (tipo === 'archivo') {
      return (
        <div key={idx} className="clases-preview-material-item clases-preview-archivo">
          <span className="clases-preview-material-badge">{getTipoMaterialLabel(mat.tipo)}</span>
          <span className="clases-preview-archivo-nombre">📎 {contenido || 'Archivo'}</span>
        </div>
      );
    }
    if (tipo === 'texto' && /<[a-z][\s\S]*>/i.test(contenido)) {
      return (
        <div key={idx} className="clases-preview-material-item">
          <span className="clases-preview-material-badge">{getTipoMaterialLabel(mat.tipo)}</span>
          <div className="clases-preview-texto"><HtmlContent content={contenido} tag="div" /></div>
        </div>
      );
    }
    return (
      <div key={idx} className="clases-preview-material-item">
        <span className="clases-preview-material-badge">{getTipoMaterialLabel(mat.tipo)}</span>
        <div className="clases-preview-texto">{contenido || '—'}</div>
      </div>
    );
  };

  return (
    <div className="clases-container">
      <div className="clases-form-page-header">
        <button type="button" className="clases-btn clases-btn-secondary" onClick={() => navigate('/dashboard/clases')}>
          ← Volver
        </button>
        <h1>{isEdit ? 'Editar Clase' : 'Nueva Clase'}</h1>
      </div>
      <ConfirmPopup
        open={Boolean(error)}
        title="Error"
        message={error}
        confirmLabel="Aceptar"
        cancelLabel={null}
        onConfirm={() => setError('')}
        onCancel={() => setError('')}
        variant="danger"
      />
      <div className="clases-form-with-preview">
      <form onSubmit={handleSubmit} className="clases-form clases-form-page">
        <div className="clases-form-group">
          <label htmlFor="titulo">Título *</label>
          <input type="text" id="titulo" name="titulo" value={formData.titulo} onChange={handleInputChange} required maxLength={200} />
        </div>
        <div className="clases-form-group">
          <label htmlFor="descripcion">Descripción</label>
          <RichTextEditor
            value={formData.descripcion}
            onChange={(html) => setFormData(prev => ({ ...prev, descripcion: html }))}
            placeholder="Descripción de la clase..."
            minHeight="140px"
            allowImageUpload
          />
        </div>
        <div className="clases-form-group">
          <label htmlFor="curso">Curso {!isEdit && '*'}</label>
          {isEdit ? (
            <p className="clases-form-curso-readonly">
              {cursos.find(c => c.id === formData.curso)?.nombre || '—'}
            </p>
          ) : (
            <select id="curso" name="curso" value={formData.curso || ''} onChange={handleCursoChange} required>
              <option value="">Seleccionar curso</option>
              {cursos.map(curso => (
                <option key={curso.id} value={curso.id}>
                  {curso.nombre}{curso.codigo ? ` (${curso.codigo})` : ''}
                </option>
              ))}
            </select>
          )}
        </div>
        {formData.curso && (
          <div className="clases-form-group clases-form-reutilizar">
            <label htmlFor="reutilizar_clase">Carpeta de material (mismo código)</label>
            <p className="clases-form-help">
              {reutilizarCodigo
                ? `Reutilizá título, descripción y material (texto, código, enlaces, videos y archivos) de otras ediciones con código “${reutilizarCodigo}”.`
                : 'Este curso no tiene código. Para compartir material entre ediciones, asignales el mismo código al crear/editar el curso.'}
            </p>
            {reutilizarCodigo && (
              <div className="clases-form-reutilizar-row">
                <select
                  id="reutilizar_clase"
                  className="clases-material-select"
                  value={reutilizarId}
                  onChange={(e) => setReutilizarId(e.target.value)}
                  disabled={loadingReutilizar || reutilizables.length === 0}
                >
                  <option value="">
                    {loadingReutilizar
                      ? 'Cargando…'
                      : (reutilizables.length === 0 ? 'No hay clases previas' : 'Elegir clase a reutilizar…')}
                  </option>
                  {reutilizables.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.titulo} — {c.curso_nombre}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="clases-btn clases-btn-secondary"
                  onClick={aplicarClaseReutilizable}
                  disabled={!reutilizarId || submitting}
                >
                  Aplicar contenido
                </button>
              </div>
            )}
          </div>
        )}
        <div className="clases-form-row">
          <div className="clases-form-group">
            <label htmlFor="fecha_programada">Fecha Programada</label>
            <input type="datetime-local" id="fecha_programada" name="fecha_programada" value={formData.fecha_programada} onChange={handleInputChange} disabled={formData.publicada} />
            <small className="clases-form-help">{formData.publicada ? 'Desmarque "Publicar ahora" para programar' : 'Dejar vacío para no programar'}</small>
          </div>
          <div className="clases-form-group">
            <label className="clases-checkbox-label">
              <input type="checkbox" name="publicada" checked={formData.publicada} onChange={handleInputChange} />
              <span>Publicar ahora</span>
            </label>
          </div>
        </div>
        <div className="clases-form-group">
          <label>Material</label>
          <div className="clases-material-form">
            <div className="clases-material-row">
              <select name="tipo" value={nuevoMaterial.tipo} onChange={handleMaterialChange} className="clases-material-select">
                <option value="texto">📝 Texto</option>
                <option value="codigo">💻 Código</option>
                <option value="video">🎬 Video (YouTube)</option>
                <option value="enlace">🔗 Enlace</option>
                <option value="archivo">📎 Archivo</option>
              </select>
              {nuevoMaterial.tipo === 'codigo' && (
                <select name="lenguaje" value={nuevoMaterial.lenguaje || 'plaintext'} onChange={handleMaterialChange} className="clases-material-select clases-material-lenguaje">
                  {LENGUAJES_CODIGO.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              )}
            </div>
            <div className="clases-material-editor">
              {nuevoMaterial.tipo === 'texto' && (
                <div className="clases-material-texto-wrap">
                  <RichTextEditor
                    value={nuevoMaterial.contenido || ''}
                    onChange={(html) => setNuevoMaterial(prev => ({ ...prev, contenido: html }))}
                    placeholder="Escriba el texto del material..."
                    minHeight="180px"
                    allowImageUpload
                  />
                </div>
              )}
              {nuevoMaterial.tipo === 'codigo' && (
                <div className="clases-material-codigo-wrap">
                  <CodeEditor
                    value={nuevoMaterial.contenido || ''}
                    onChange={(code) => setNuevoMaterial(prev => ({ ...prev, contenido: code }))}
                    language={nuevoMaterial.lenguaje || 'plaintext'}
                    placeholder="Escriba o pegue el código..."
                    className="clases-material-codigo-editor"
                  />
                </div>
              )}
              {nuevoMaterial.tipo === 'video' && (
                <input
                  type="url"
                  name="contenido"
                  value={nuevoMaterial.contenido || ''}
                  onChange={handleMaterialChange}
                  placeholder="https://www.youtube.com/watch?v=... o https://youtu.be/..."
                  className="clases-material-input clases-material-input-video"
                />
              )}
              {nuevoMaterial.tipo === 'enlace' && (
                <div className="clases-material-enlace-campos">
                  <input
                    type="text"
                    name="contenido"
                    value={nuevoMaterial.contenido || ''}
                    onChange={handleMaterialChange}
                    placeholder="URL del enlace (https://...)"
                    className="clases-material-input"
                  />
                  <input
                    type="text"
                    name="titulo"
                    value={nuevoMaterial.titulo || ''}
                    onChange={handleMaterialChange}
                    placeholder="Texto a mostrar (opcional). Ej: Documentación de MySQL"
                    className="clases-material-input"
                    maxLength={200}
                  />
                  <small className="clases-form-help">Si no escribís un texto, se muestra la URL completa.</small>
                </div>
              )}
              {nuevoMaterial.tipo === 'archivo' && (
                <>
                  <input
                    ref={materialArchivoInputRef}
                    type="file"
                    className="clases-material-file-input-hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setNuevoMaterial(prev => ({ ...prev, contenido: file.name, _file: file }));
                        setError('');
                      }
                      e.target.value = '';
                    }}
                  />
                  <button
                    type="button"
                    className="clases-btn clases-btn-secondary clases-btn-small"
                    onClick={() => materialArchivoInputRef.current?.click()}
                  >
                    📎 Seleccionar archivo
                  </button>
                  {nuevoMaterial.contenido && (
                    <span className="clases-material-archivo-nombre">{nuevoMaterial.contenido}</span>
                  )}
                </>
              )}
            </div>
            <div className="clases-material-actions">
              <button type="button" onClick={agregarMaterial} className="clases-btn clases-btn-secondary clases-btn-small">➕ Agregar material</button>
            </div>
          </div>
          {formData.material.length > 0 && (
            <div className="clases-material-list">
              {formData.material.map((mat, index) => (
                <div key={index} className="clases-material-item">
                  <div className="clases-material-item-order">
                    <button type="button" onClick={() => moverMaterial(index, 'up')} className="clases-btn-order" title="Subir" disabled={index === 0}>↑</button>
                    <button type="button" onClick={() => moverMaterial(index, 'down')} className="clases-btn-order" title="Bajar" disabled={index === formData.material.length - 1}>↓</button>
                  </div>
                  <span className="clases-material-badge">{getTipoMaterialLabel(mat.tipo)}</span>
                  <span
                    className="clases-material-contenido"
                    title={mat.tipo === 'enlace' ? (mat.contenido || '') : undefined}
                  >
                    {mat.tipo === 'codigo' && mat.lenguaje ? `[${mat.lenguaje}] ` : ''}
                    {mat.tipo === 'enlace' && (mat.titulo || '').trim()
                      ? (mat.titulo || '').trim()
                      : (typeof mat.contenido === 'string' && mat.contenido.length > 50 ? `${mat.contenido.substring(0, 50)}...` : (mat.contenido || ''))}
                  </span>
                  <button type="button" onClick={() => eliminarMaterial(index)} className="clases-btn-remove" title="Eliminar">✕</button>
                </div>
              ))}
            </div>
          )}
        </div>
        {submitting && (
          <div className="clases-form-upload-progress">
            <UploadProgress
              percent={uploadProgress}
              label={uploadProgress >= 100 ? 'Listo' : `Subiendo… ${uploadProgress}%`}
            />
          </div>
        )}
        <div className="clases-modal-actions">
          <button type="button" className="clases-btn clases-btn-secondary" onClick={() => navigate('/dashboard/clases')} disabled={submitting}>Cancelar</button>
          <button type="submit" className="clases-btn clases-btn-primary" disabled={submitting}>
            {submitting ? (uploadProgress >= 100 ? 'Guardando...' : 'Subiendo...') : (isEdit ? 'Actualizar' : 'Crear')}
          </button>
        </div>
      </form>
      <aside className="clases-form-preview">
        <h3 className="clases-form-preview-title">Vista previa</h3>
        <div className="clases-form-preview-content">
          <h2 className="clases-preview-titulo">{formData.titulo || 'Título de la clase'}</h2>
          {formData.descripcion ? (
            <div className="clases-preview-desc"><HtmlContent content={formData.descripcion} tag="div" /></div>
          ) : (
            <p className="clases-preview-sin-desc">Sin descripción</p>
          )}
          {formData.material.length > 0 && (
            <div className="clases-preview-material">
              <strong>Material</strong>
              {formData.material.map((mat, idx) => renderPreviewMaterial(mat, idx))}
            </div>
          )}
        </div>
      </aside>
      </div>
    </div>
  );
};

export default ClaseForm;
